import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";

const AUDIENCE_TYPE_LABELS = {
  members: "Members",
  tracker: "Journey",
  programme: "Programme",
  all: "All members",
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const AudienceCell = ({ target }) => {
  const type = target?.type || "";
  const label = target?.label || "";
  const showLabel = label && type !== "all";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Chip
        size="small"
        label={AUDIENCE_TYPE_LABELS[type] || type || "—"}
        sx={{ alignSelf: "flex-start" }}
      />
      {showLabel ? (
        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
          {label}
        </Typography>
      ) : null}
    </Box>
  );
};

const StateRow = ({ children }) => (
  <TableRow>
    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
      {children}
    </TableCell>
  </TableRow>
);

function HistoryRows({ items, isLoading, error, onRetry }) {
  if (isLoading) {
    return (
      <StateRow>
        <CircularProgress size={28} />
      </StateRow>
    );
  }
  if (error) {
    return (
      <StateRow>
        <Typography sx={{ color: "error.main", mb: 1 }}>{error}</Typography>
        <Button size="small" variant="outlined" onClick={onRetry}>
          Try again
        </Button>
      </StateRow>
    );
  }
  if (!items.length) {
    return (
      <StateRow>
        <Typography sx={{ color: "text.secondary" }}>
          No notifications sent yet
        </Typography>
      </StateRow>
    );
  }
  return items.map((item) => (
    <TableRow key={item._id} hover>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        {item.createdAt ? moment(item.createdAt).format("DD MMM YYYY, hh:mm A") : "—"}
      </TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{item.title}</Typography>
        <Typography
          title={item.message}
          sx={{
            fontSize: 12.5,
            color: "text.secondary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.message}
        </Typography>
      </TableCell>
      <TableCell>
        <AudienceCell target={item.target} />
      </TableCell>
      <TableCell align="right">{item.sentCount ?? 0}</TableCell>
      <TableCell>{item.sentBy?.name || "—"}</TableCell>
    </TableRow>
  ));
}

export default function SentNotificationsTable({
  items,
  total,
  page,
  rowsPerPage,
  isLoading,
  error,
  onPageChange,
  onRowsPerPageChange,
  onRetry,
}) {
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Audience</TableCell>
              <TableCell align="right">Recipients</TableCell>
              <TableCell>Sent by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <HistoryRows
              items={items}
              isLoading={isLoading}
              error={error}
              onRetry={onRetry}
            />
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(event, nextPage) => onPageChange(nextPage)}
        onRowsPerPageChange={(event) =>
          onRowsPerPageChange(parseInt(event.target.value, 10))
        }
      />
    </>
  );
}
